import { ZanshinPairCard } from '@/components/zanshin/ZanshinPairCard'
import { ZanshinStateBadge } from '@/components/zanshin/ZanshinStateBadge'
import type { ZanshinPair } from '@/services/zanshin.service'

/**
 * Pagina demo admin-only: mostra TUTTI gli stati Zanshin con dati mockati.
 * Utile per QA visiva (verifica colori/icone su sfondo scuro reale) e per
 * onboarding di nuovi utenti senza dover aspettare operazioni reali.
 *
 * Route: /admin/zanshin-demo (gated da AdminOnlyRoute in App.tsx).
 */

const mkOp = (state: any, shieldPhase: 0 | 1 | 2, overrides: any = {}) => ({
  operation_id: 142,
  zanshin_state: state,
  shield_phase: shieldPhase,
  prop_direction: 'BUY' as const,
  prop_entry_price: 4720.50,
  prop_lots: 0.10,
  prop_sl_current: 4715.00,
  expected_prop_sl: 4715.00,
  prop_tp_current: 4750.00,
  broker_entry_price: 4720.30,
  broker_lots: 0.10,
  broker_sl_current: 4735.00,
  expected_broker_sl: 4735.00,
  broker_tp_current: null,
  is_pending: false,
  pending_age_s: null,
  stale_cycles: 0,
  last_decision_ts: '2026-05-15T07:30:00Z',
  last_decision_action: 'MOVE_TO_BE',
  last_decision_event: 'CONFIRMED',
  last_decision_reason: 'shield phase F1: lock 20% gain',
  status_updated_at: '2026-05-15T07:30:00Z',
  ...overrides,
})

const cards: ZanshinPair[] = [
  { pair_id: 1, display_name: 'Test1Prop200k', prop_provider_label: 'FunderPro',
    prop_phase_label: 'Challenge', prop_ea_connected: false, broker_ea_connected: false,
    operation: null },
  { pair_id: 2, display_name: 'FundedNext $100K', prop_provider_label: 'FundedNext',
    prop_phase_label: 'Real pre-payout', prop_ea_connected: true, broker_ea_connected: true,
    operation: mkOp('HEDGE', 0) },
  { pair_id: 3, display_name: 'FunderPro $200K', prop_provider_label: 'FunderPro',
    prop_phase_label: 'Challenge', prop_ea_connected: true, broker_ea_connected: true,
    operation: mkOp('BROKER_SHIELD', 1) },
  { pair_id: 4, display_name: 'FPMarkets $50K', prop_provider_label: 'FPMarkets',
    prop_phase_label: 'Real post-payout', prop_ea_connected: true, broker_ea_connected: true,
    operation: mkOp('BROKER_SHIELD', 2, { is_pending: true, pending_age_s: 4.2 }) },
  { pair_id: 5, display_name: 'TMGM $100K', prop_provider_label: 'TMGM',
    prop_phase_label: 'Challenge', prop_ea_connected: true, broker_ea_connected: false,
    operation: mkOp('RUNNER_BROKER', 2, { is_pending: true, pending_age_s: 8.7 }) },
  { pair_id: 6, display_name: 'FundedNext $200K', prop_provider_label: 'FundedNext',
    prop_phase_label: 'Real pre-payout', prop_ea_connected: true, broker_ea_connected: true,
    operation: mkOp('RUNNER_PROP', 2, { is_pending: true, pending_age_s: 14.5 }) },
  { pair_id: 7, display_name: 'FunderPro $25K', prop_provider_label: 'FunderPro',
    prop_phase_label: 'Real post-payout', prop_ea_connected: true, broker_ea_connected: true,
    operation: mkOp('RUNNER_ATTIVO', 2) },
]

export function ZanshinDemo() {
  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-1">🎨 Zanshin — Demo stati</h1>
        <p className="text-sm text-muted-foreground">
          Tutti gli stati renderizzati con dati mockati. Pagina di QA visiva e
          onboarding utenti. Nessuna chiamata API: solo presentazione.
        </p>
      </div>

      <section>
        <h2 className="text-base font-semibold mb-3 text-foreground">
          Card complete (vista dashboard)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map(p => <ZanshinPairCard key={p.pair_id} pair={p} />)}
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold mb-3 text-foreground">
          Badge inline (vista Open Positions)
        </h2>
        <div className="flex flex-wrap gap-2">
          {cards.filter(c => c.operation).map(p => (
            <ZanshinStateBadge key={p.pair_id} pair={p} />
          ))}
        </div>
      </section>
    </div>
  )
}
